import { type FetchBaseQueryError } from "@reduxjs/toolkit/query"

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null
}

export function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return isObject(error) && "status" in error
}

export function isErrorWithMessage(error: unknown): error is { message: string } {
  return isObject(error) && "message" in error && typeof error.message === "string"
}

function hasErrorProperty(error: unknown): error is { error: string } {
  return isObject(error) && "error" in error && typeof error.error === "string"
}

function hasDataObject(error: unknown): error is { data: Record<string, unknown> } {
  return isObject(error) && "data" in error && isObject(error.data)
}

function hasDataWithMessage(error: unknown): error is { data: { message: string } } {
  return hasDataObject(error) && isErrorWithMessage(error.data)
}

function hasDataWithErrorString(error: unknown): error is { data: { error: string } } {
  return hasDataObject(error) && hasErrorProperty(error.data)
}

function hasDataWithErrorArray(
  error: unknown
): error is { data: { error: Array<{ message: string; code?: string }> } } {
  return (
    hasDataObject(error) &&
    "error" in error.data &&
    Array.isArray(error.data.error) &&
    error.data.error.length > 0 &&
    isErrorWithMessage(error.data.error[0])
  )
}

export const INSUFFICIENT_PURCHASED_BALANCE_CODE = "insufficient_purchased_balance"

export function isInsufficientPurchasedBalanceError(error: unknown): boolean {
  if (!hasDataWithErrorArray(error)) return false
  return error.data.error[0].code === INSUFFICIENT_PURCHASED_BALANCE_CODE
}

export const GENERIC_ERROR_MESSAGE = "An unexpected error occurred"

export const isGenericError = (error: unknown): boolean => {
  if (isObject(error) && "data" in error && isObject(error.data) && "isGenericError" in error.data) {
    return error.data.isGenericError === true
  }
  return false
}

/**
 * Extract error message from various API error formats
 * Handles: string, {data: {error: [{message}]}}, {data: {message}}, {message}, {data: {error: string}}, {error: string}
 */
export function extractErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    return error
  }

  if (hasDataWithErrorArray(error)) {
    return error.data.error[0].message
  }

  if (hasDataWithMessage(error)) {
    return error.data.message
  }

  if (hasDataWithErrorString(error)) {
    return error.data.error
  }

  if (isErrorWithMessage(error)) {
    return error.message
  }

  if (hasErrorProperty(error)) {
    return error.error
  }

  return GENERIC_ERROR_MESSAGE
}
